import React, {useState, useContext, useEffect} from 'react';
import { Box, Typography, Paper, Button, TextField, CircularProgress } from '@mui/material';
import useAxios from '../../../hooks/useAxios';
import AuthContext from '../../../contexts/auth-context';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { urls, toastify_status } from '../../config/variables';
import NewEncounterFormStyle from './NewEncounterForm.module';
import Toastify from '../../ui/Toastify';
import { validateNlpResponse } from '../../../Utils/validateHttpResponse';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { globalColors } from '../../../constants/ColorsPalette';
import * as motion from "motion/react-client"
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

const NLPDisplay = ({ populateDiagnose, handleRemovePopulatedDiagnose, filteredDiagnoses }) => {
  const [data, setData] = useState({ field1: "" });
  const snoicd = 'icd10'
  const [response, setResponse] = useState('');
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [error, setError] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const { operation } = useAxios();
  const authCtx = useContext(AuthContext);
  const classes = NewEncounterFormStyle();
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation('newEncounter');

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 2

  const totalPages = (filteredEntities && filteredEntities.length > 0) && Math.ceil(filteredEntities.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentItems = (filteredEntities && filteredEntities.length > 0) && filteredEntities.slice(startIndex, endIndex)

  const handleChange = (e) => {
    setData({ field1: e.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(undefined);
    
    await operation({
      method: 'POST',
      url: urls.NLP,
      headers: { Authorization: `token ${authCtx.token}` },
      data: {
        content: data.field1,
        response: snoicd
      }
    }, handleNlpResponse, setError);
    
    setIsLoading(false);
  };

  const handleNlpResponse = (response) => {
    if(validateNlpResponse(response)) {
      setResponse(response)
    } else {
      Toastify({message: 'invalid NLP data response', state: toastify_status.ERROR})
    }
  }

  // populate diagnoses when filteredDiagnoses less than 10
  const handleSelectIcd = (icd) => {
    if(filteredDiagnoses.length < 10) {
      populateDiagnose({ icdcode: icd.Code, shortdescription: icd.Description })
    } else {
      Toastify({message: 'You have reached the maximam diagnoses.', state: toastify_status.WARNING})
    }
  }

  // remove a specefic populated diagnose
  const handleRemoveIcd = (icd) => {
    handleRemovePopulatedDiagnose(icd.Code)
  }

  // change page
  const handleChangePage = (event, value) => {
    setCurrentPage(value);
  };

  // filterign entities
  useEffect(() => {
    setCurrentPage(1)
    if(searchQuery) {
      setFilteredEntities(
        response?.response?.Entities.filter((entity) =>
          entity.Text
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        )
      );
    } else {
      setFilteredEntities(response?.response?.Entities)
    }
    // eslint-disable-next-line
  }, [searchQuery, response])

  return (
    <Box sx={classes.nlp_container}>
      <Typography sx={classes.nlp_title}>
        NLP Analysis
      </Typography>

      {/* Input Section */}
      <Paper sx={classes.nlp_form_container}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Enter medical text for analysis"
            placeholder="Enter the medical text you want to analyze..."
            onChange={handleChange}
            sx={classes.nlp_form_input}
          />
          <Button
            variant="contained"
            color="primary"
            size="medium"
            type="submit"
            disabled={isLoading || !data.field1.trim()}
            startIcon={isLoading && <CircularProgress size={20} color="inherit" />}
            sx={classes.nlp_form_button}
          >
            {isLoading ? 'Analyzing...' : 'Analyze Text'}
          </Button>
        </form>
      </Paper>

      {error ? <Alert severity="error">
        <AlertTitle>NLP Analysis Error</AlertTitle>
      </Alert>
        : <Box>
          <Box sx={classes.nlp_search_and_pagination_box}>
            {response && response?.response?.Entities.length > 0 && <Box sx={classes.nlp_search_box}>
              <TextField
                variant="outlined"
                InputProps={{
                  startAdornment: searchQuery && (
                    <InputAdornment position="start">
                      <IconButton
                        onClick={() => setSearchQuery('')}
                        sx={classes.clear_button}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder={t('Search')}
                size="small"
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
                sx={classes.nlp_search_input}
              />
            </Box>}
            {filteredEntities && (totalPages > 1) &&
              <Box sx={classes.nlp_pagination}>
                <Stack spacing={2}>
                  <Pagination size="medium" count={totalPages} page={currentPage} onChange={handleChangePage} />
                </Stack>
              </Box>
            }
          </Box>
          <motion.div
            key={currentPage}
            initial={classes.nlp_entity_container_animation.initial}
            animate={classes.nlp_entity_container_animation.animate}
            transition={classes.nlp_entity_container_animation.transition}
          >
            {filteredEntities?.length === 0 &&
            <Alert severity="info">
              <AlertTitle>No Entities found</AlertTitle>
            </Alert>}
      <Box sx={{...classes.nlp_entities_container, ...((response && response?.response?.Entities.length > 0) ? classes.nlp_entities_container_height : {})}}>
              {currentItems && currentItems.map((entity, index) => {
                const confidenceScore = Math.round(entity.Score * 100);
                return (
                  <Box key={index} sx={classes.nlp_entity_box}>
                    <Typography sx={classes.nlp_entity_title}>
                      {entity.Text}
                    </Typography>
                    <Box sx={classes.nlp_box}>
                      <Typography sx={classes.nlp_entity_primary_text}>
                        Confidence Score: <span style={{ ...classes.nlp_entity_secondary_text, color: confidenceScore > 80 ? globalColors.greenPea : confidenceScore > 60 ? globalColors.amber : globalColors.red }}>{(entity.Score * 100).toFixed(2)}%</span>
                      </Typography>
                      {entity.Traits.map((trait, index) => (
                        <Typography key={index} sx={classes.nlp_entity_primary_text}>
                          Traits: <span style={classes.nlp_entity_secondary_text}>{trait.Name}</span>
                        </Typography>
                      ))}
                    </Box>

                    <Box sx={classes.nlp_box}>
                      <Typography sx={classes.nlp_icds_box_title}>
                        Possible ICD-10 Codes:
                      </Typography>
                      {entity.ICD10CMConcepts.map((icd, index) => {
                        const selectedIcd = filteredDiagnoses.find((icdObj) => icdObj.icd === icd.Code)
                        return <motion.div
                          key={index}
                          initial={classes.nlp_icd_box_animation.initial}
                          animate={classes.nlp_icd_box_animation.animate}
                          transition={{ duration: (0.1 * index) + 0.4 }}
                        >
                          <Tooltip
                            placement="top"
                            title={
                              <Typography sx={classes.nlp_icds_box_tooltip}>
                                {selectedIcd ? 'click to remove populated icd' : 'click to populate'}
                              </Typography>
                            }
                          >
                            <Box onClick={() => selectedIcd ? handleRemoveIcd(icd) : handleSelectIcd(icd)} sx={{ ...classes.nlp_icd_box, ...(selectedIcd ? classes.nlp_icd_box_active : {}) }}>
                              <Typography sx={classes.nlp_icd_primary_text}>
                                {icd.Code} - <span style={classes.nlp_entity_secondary_text}>{icd.Description}</span>
                              </Typography>
                              <Typography sx={classes.nlp_entity_secondary_text}>Match: {(icd.Score * 100).toFixed(2)}%</Typography>
                            </Box>
                          </Tooltip>
                        </motion.div>
                      })}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </motion.div>
        </Box>
      }
    </Box>
  );
};

export default NLPDisplay; 